import { useCallback, useState } from "react";

/**
 * Tracks whether an imperative polling loop (fetch-in-a-setInterval, not
 * react-query) is currently failing to reach the server, so a page can show
 * a compact "you're looking at old data" hint instead of silently swallowing
 * the poll failure. Mirrors the react-query-backed
 * `features/_mv2/mv2Freshness.js` for pages that poll by hand.
 *
 * The data already on screen is left untouched — this only tracks the
 * connection signal, never clears or overrides state.
 */
export function usePollFreshness() {
  const [state, setState] = useState({ isStale: false, lastSuccessMs: null });

  const markSuccess = useCallback(() => {
    setState({ isStale: false, lastSuccessMs: Date.now() });
  }, []);

  /** No-op until a first success has landed: a first-load failure is a hard error for the page to show, not a staleness notice. */
  const markFailure = useCallback(() => {
    setState((prev) => (prev.lastSuccessMs && !prev.isStale ? { ...prev, isStale: true } : prev));
  }, []);

  return { isStale: state.isStale, lastSuccessMs: state.lastSuccessMs, markSuccess, markFailure };
}
